import { memo, type MouseEvent, useCallback, useMemo } from 'react';
import { Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { HighwayArea } from '@thive/fddm-types';
import FlipIcon from '@mui/icons-material/Flip';

import {
  LaneLeftHandIcon,
  LaneLeftRightIcon,
  LaneRightHandIcon,
  LaneRightIcon,
} from '@/assets/icons';
import { minItemsArrayUtils } from '@/modules/floorplanDesign/helpers/minItems';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { getCommonOption } from '@/modules/common/helpers/commonValue';
import { StyledTooltip } from '@/modules/common/components/StyledTooltip';
import {
  createEmptyAreas,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers/helpers';
import { useTranslation } from 'react-i18next';

type HighwayRoadType = HighwayArea['highwayPoiSpecs'][number]['roadType'];

type LaneDirectionProps = {
  selectedHighways: HighwayArea[];
};

const controlsSx = {
  pointerEvents: 'auto',
};

// A flex wrapper (not inline) so the flip button stretches to the toggle group's height
const FLIP_WRAPPER_STYLE = { display: 'flex' };

/** @description Translation key explaining why flipping is not possible, or undefined when it is. */
const getFlipDisabledReasonKey = (roadType: HighwayRoadType | null): string | undefined => {
  if (roadType === null) return 'interface:properties.highway.tooltips.flip_disabled_mixed';
  if (roadType === 'TWO_WAY_INDIA_STYLE')
    return 'interface:properties.highway.tooltips.flip_disabled_india_style';
  return undefined;
};

function LaneDirectionComponent({ selectedHighways }: LaneDirectionProps) {
  const { t } = useTranslation();
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const mode = useAppStore((state) => state.mode);

  const selectedHighwaysSegmentsIds = useMemo(
    () =>
      new Set(selectedHighways.flatMap((highway) => highway.segments.map((segment) => segment.id))),
    [selectedHighways],
  );

  const hasJitRouting = useFloorplanDesignStore(
    useCallback(
      (state) =>
        state.design.justInTimeRoutingConfigs.some((jitRoutingConfig) =>
          selectedHighwaysSegmentsIds.has(jitRoutingConfig.highwayAreaId),
        ),
      [selectedHighwaysSegmentsIds],
    ),
  );

  const isPicking = useAppStore((state) => state.selectedToolId === 'choose');

  const roadType = useMemo(() => {
    const values = selectedHighways.map((highway) => highway.highwayPoiSpecs[0].roadType);
    return getCommonOption(values);
  }, [selectedHighways]);

  const isDisabled = mode !== 'EDITABLE';
  const roadTypeDisabled = isDisabled || hasJitRouting || isPicking;
  const flipDisabledReasonKey = getFlipDisabledReasonKey(roadType);
  const flipDisabled = flipDisabledReasonKey !== undefined;

  const setRoadType = useCallback(
    (highways: HighwayArea[], newValue: HighwayRoadType) => {
      fddmSvc.setPreference('highwayArea.highwayPoiSpecs.roadType', newValue);

      const isOneWay = newValue === 'ONE_WAY';
      const connectUturns: HighwayArea['connectUturns'] = isOneWay
        ? 'NO_UTURN_MAPPING'
        : fddmSvc.getPreference('highwayArea.connectUturns');

      // Only persist the preference when it's a meaningful 2-way value
      if (!isOneWay) {
        fddmSvc.setPreference('highwayArea.connectUturns', connectUturns);
      }

      const updatedAreas = createEmptyAreas();
      highways.forEach((highway) => {
        pushAreaToItsCollection(updatedAreas, {
          ...highway,
          connectUturns,
          highwayPoiSpecs: minItemsArrayUtils.mapAtLeast1(highway.highwayPoiSpecs, (spec) => ({
            ...spec,
            roadType: newValue,
          })),
        });
      });

      fddmSvc.updateAreas(updatedAreas);
    },
    [fddmSvc],
  );

  const withFlippedDirection = (highway: HighwayArea): HighwayArea => {
    const { segments } = highway;

    return {
      ...highway,
      segments: minItemsArrayUtils.mapAtLeast1(segments, (segment, index) => ({
        ...segment,
        start: segments[segments.length - 1 - index].end,
        end: segments[segments.length - 1 - index].start,
        height: segments[segments.length - 1 - index].height,
      })),
    };
  };

  const flipHighwayDirection = useCallback(
    (highways: HighwayArea[]) => {
      const updatedAreas = createEmptyAreas();
      highways.forEach((highway) => {
        pushAreaToItsCollection(updatedAreas, withFlippedDirection(highway));
      });

      fddmSvc.updateAreas(updatedAreas);
    },
    [fddmSvc],
  );

  const onRoadTypeChange = useCallback(
    (_event: MouseEvent<HTMLElement>, input: HighwayRoadType | null) => {
      if (roadTypeDisabled) return;
      if (!input || input === roadType) return;

      setRoadType(selectedHighways, input);
    },
    [roadType, roadTypeDisabled, selectedHighways, setRoadType],
  );

  const onFlipHighway = useCallback(() => {
    if (isDisabled || flipDisabled) return;

    if (roadType === 'TWO_WAY_RIGHT_HAND_TRAFFIC') {
      setRoadType(selectedHighways, 'TWO_WAY_LEFT_HAND_TRAFFIC');
      return;
    }

    if (roadType === 'TWO_WAY_LEFT_HAND_TRAFFIC') {
      setRoadType(selectedHighways, 'TWO_WAY_RIGHT_HAND_TRAFFIC');
      return;
    }

    flipHighwayDirection(selectedHighways);
  }, [flipDisabled, flipHighwayDirection, isDisabled, roadType, selectedHighways, setRoadType]);

  return (
    <Stack direction='row' spacing={1} sx={controlsSx}>
      <ToggleButtonGroup value={roadType} exclusive onChange={onRoadTypeChange}>
        <ToggleButton
          value='TWO_WAY_INDIA_STYLE'
          aria-label={t<string>(
            'interface:properties.highway.lane_direction_options.two_way_india_style',
            'Two way india style',
          )}
          disabled={roadTypeDisabled}
        >
          <LaneLeftRightIcon />
        </ToggleButton>

        {roadType === 'TWO_WAY_LEFT_HAND_TRAFFIC' ? (
          <ToggleButton
            value='TWO_WAY_LEFT_HAND_TRAFFIC'
            aria-label={t<string>(
              'interface:properties.highway.lane_direction_options.two_way_left_hand_traffic',
              'Two way left hand traffic',
            )}
            disabled={roadTypeDisabled}
          >
            <LaneLeftHandIcon />
          </ToggleButton>
        ) : (
          <ToggleButton
            value='TWO_WAY_RIGHT_HAND_TRAFFIC'
            aria-label={t<string>(
              'interface:properties.highway.lane_direction_options.two_way_right_hand_traffic',
              'Two way right hand traffic',
            )}
            disabled={roadTypeDisabled}
          >
            <LaneRightHandIcon />
          </ToggleButton>
        )}

        <ToggleButton
          value='ONE_WAY'
          aria-label={t<string>(
            'interface:properties.highway.lane_direction_options.one_way',
            'One way',
          )}
        >
          <LaneRightIcon />
        </ToggleButton>
      </ToggleButtonGroup>

      <StyledTooltip
        titleKey={flipDisabledReasonKey}
        placement='top'
        disabled={isDisabled || flipDisabled}
        openOnlyWhenDisabled
      >
        <span style={FLIP_WRAPPER_STYLE}>
          <ToggleButton
            value='FLIP'
            disabled={isDisabled || flipDisabled}
            onClick={onFlipHighway}
            aria-label={t<string>(
              'interface:properties.highway.lane_direction_options.flip',
              'Flip',
            )}
          >
            <FlipIcon />
          </ToggleButton>
        </span>
      </StyledTooltip>
    </Stack>
  );
}

export const LaneDirection = memo(LaneDirectionComponent);
